/**
 * 8D CAR scoring fields for the Corrective Action Report.
 *
 * The customer grades the supplier's 8D across four 1–5 dimensions when
 * reviewing a submitted CAR (CR-7357, was two before):
 *   ORC — Occurrence Root Cause        (rc_score)
 *   ERC — Escape Root Cause            (erc_score)
 *   OPA — Occurrence Prevention Actions (ca_score)
 *   EPA — Escape Prevention Actions    (epa_score)
 * The rc_score / ca_score field names are kept for ORC / OPA so existing data
 * and integrations are unaffected. All four are persisted on the NCR and
 * surfaced as columns in the Quality NCR list.
 *
 * Rendered in one of two modes:
 * - `editable` — the customer is reviewing a CAR at status `car_submitted`
 *   and may still change the grades.
 * - read-only — the review window has closed or the viewer is not the
 *   reviewing customer. The grades still render so the score stays auditable.
 */

import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { SCORE_DIMENSIONS, scoreOptions } from './scoreCriteria';

/** 8D scoring dropdown options (1-5), sourced from the shared score-criteria
 *  definitions (CR-7356) so the dropdowns, the supplier guidance panels and
 *  the list columns all read off one file. */
export const RC_SCORE_OPTIONS = scoreOptions(SCORE_DIMENSIONS.ORC);
export const ERC_SCORE_OPTIONS = scoreOptions(SCORE_DIMENSIONS.ERC);
export const CA_SCORE_OPTIONS = scoreOptions(SCORE_DIMENSIONS.OPA);
export const EPA_SCORE_OPTIONS = scoreOptions(SCORE_DIMENSIONS.EPA);

/** The two grades every other tenant scores, with the wording they had before
 *  the DE 8D redesign (CR-7356/7357). Kept verbatim rather than re-pointed at
 *  the shared criteria: a client who has not asked for the four-dimension 8D
 *  should see no part of it, dropdown help text included. */
export const LEGACY_RC_SCORE_OPTIONS = [
  { value: '1', label: '1 — Insufficient (symptom only, e.g. "human error")' },
  { value: '2', label: '2' },
  { value: '3', label: '3 — Basic (partial cause identified)' },
  { value: '4', label: '4' },
  { value: '5', label: '5 — Excellent (proven root cause with data + validation)' },
];

export const LEGACY_CA_SCORE_OPTIONS = [
  { value: '1', label: '1 — Administrative (training, instruction)' },
  { value: '2', label: '2' },
  { value: '3', label: '3 — Process Improvement' },
  { value: '4', label: '4' },
  { value: '5', label: '5 — System-level fix (error-proofing)' },
];

/** Resolve a stored integer score to its descriptive option label. */
function scoreLabel(
  score: number | null | undefined,
  options: { value: string; label: string }[]
): string | null {
  if (score == null) return null;
  return options.find((o) => o.value === String(score))?.label ?? String(score);
}

interface CarScoreFieldsProps {
  rcScore?: number | null;
  ercScore?: number | null;
  caScore?: number | null;
  epaScore?: number | null;
  /** When false the grades render as static text instead of dropdowns. */
  editable: boolean;
  /** DE's four-dimension 8D (CR-7357). False — the default for every client
   *  without the Escape 8D switched on — renders the original two grades,
   *  RC and CA, with their original wording. */
  fourDimension?: boolean;
  onRcScoreChange?: (score: number) => void;
  onErcScoreChange?: (score: number) => void;
  onCaScoreChange?: (score: number) => void;
  onEpaScoreChange?: (score: number) => void;
}

export function CarScoreFields({
  rcScore,
  ercScore,
  caScore,
  epaScore,
  editable,
  fourDimension = false,
  onRcScoreChange,
  onErcScoreChange,
  onCaScoreChange,
  onEpaScoreChange,
}: CarScoreFieldsProps) {
  // The four dimensions in review order: the two root-cause grades, then the
  // two prevention-action grades. `id` feeds the label/select association.
  const fields = fourDimension
    ? [
        { id: 'orc-score', label: 'ORC Score', hint: 'Occurrence Root Cause', options: RC_SCORE_OPTIONS, value: rcScore, onChange: onRcScoreChange },
        { id: 'erc-score', label: 'ERC Score', hint: 'Escape Root Cause', options: ERC_SCORE_OPTIONS, value: ercScore, onChange: onErcScoreChange },
        { id: 'opa-score', label: 'OPA Score', hint: 'Occurrence Prevention Actions', options: CA_SCORE_OPTIONS, value: caScore, onChange: onCaScoreChange },
        { id: 'epa-score', label: 'EPA Score', hint: 'Escape Prevention Actions', options: EPA_SCORE_OPTIONS, value: epaScore, onChange: onEpaScoreChange },
      ]
    : [
        { id: 'rc-score', label: 'RC Score', hint: '', options: LEGACY_RC_SCORE_OPTIONS, value: rcScore, onChange: onRcScoreChange },
        { id: 'ca-score', label: 'CA Score', hint: '', options: LEGACY_CA_SCORE_OPTIONS, value: caScore, onChange: onCaScoreChange },
      ];

  if (!editable) {
    return (
      <div className="flex flex-wrap gap-6">
        {fields.map((f) => (
          <div key={f.id} className="space-y-1.5">
            <Label>{f.label}</Label>
            <p className="text-sm font-medium">{scoreLabel(f.value, f.options) ?? 'Not scored'}</p>
            {f.hint && <p className="text-xs text-muted-foreground">{f.hint}</p>}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-6 mb-4">
      {fields.map((f) => (
        <div key={f.id} className="space-y-1.5">
          <Label htmlFor={f.id}>{f.label} <span className="text-red-500">*</span></Label>
          {/* '' (not undefined) when unscored so the Select stays controlled
              from first render — Radix shows the placeholder for ''. */}
          <Select
            value={f.value != null ? String(f.value) : ''}
            onValueChange={(v) => f.onChange?.(parseInt(v))}
          >
            {/* text-left: the Radix trigger is a <button>, which centres its
                text by default. The base trigger also clamps the value with
                line-clamp-1, which truncates at a WORD boundary and leaves a
                gap before the chevron; override the value span to a plain
                left-aligned single-line truncation so the ellipsis sits at the
                edge. */}
            <SelectTrigger
              id={f.id}
              className="w-72 max-w-full text-left [&>span]:!block [&>span]:!truncate"
            >
              <SelectValue placeholder="Select 1–5" />
            </SelectTrigger>
            <SelectContent>
              {f.options.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">{f.hint}</p>
        </div>
      ))}
    </div>
  );
}
